import React, { useEffect, useRef } from 'react';
// @ts-ignore
import { KrpanoPlayer } from 'pano-sdk';
import { useHover } from 'react-use';
import { nanoid } from 'nanoid';
import produce from 'immer';
import type { SetRequired } from 'type-fest';
import { toFlag } from 'pano-utils';
import { useGate } from 'effector-react';
import {
  type Hotspot,
  type HotspotGroupItem,
  type KrpPlayer,
  type LookToView,
  MatrixFovType,
  type SceneData,
  SelectStatus,
} from '@types';
import useTourEditing from '@hooks/useTourEditing';
import Title from '@components/Title';
import { Collapse } from '@components/Collapse';
import Input from '@components/Input';
import Button from '@components/Button';
import { MainPlayer } from '@stores/krpPlayerStores';
import useInputHandlers from '@hooks/useInputHandelrs';
import { Validator } from '@components/Validator';
import FeatureToggle from '@components/FeatureToggle';
import { $sceneMapApi } from '@stores/dataStores';
import ImageContainer from '@components/ImageContainer';
import Checkbox, { CheckboxNoFunc } from '@components/Checkbox';
import { $GateEditingGroup } from '@stores/editingHotspotStores';
import PanelBase from '../../panel/PanelBase';
import {
  $GateGroupViewCapture,
  groupViewCatpure,
  onHotspotClick,
} from '../index';
import { loadScene } from '../../player';
import ItemIcon from './ItemIcon';
import getFeatName from './getFeatName';

interface Props {
  scene: SceneData;
  index: number;
  onRequestClose: () => void;
}

export function PanelGroupSetting({ scene, index, onRequestClose }: Props) {
  const hotspotList = scene.hotspot ?? [];
  const groupSetting = scene.groups ?? {};
  const groupList = groupSetting.list ?? [];
  const data = groupList[index];
  const { state, setState, isChanged } = useTourEditing(data, {
    id: `hg_${nanoid()}`,
    name: '',
    view: { type: 0 },
    hotspots: { type: 0, list: [] },
  });
  const { name, view, hotspots } = state;
  useGate($GateEditingGroup, { data: state, index });

  useEffect(() => {
    if (view.type === 1) {
      MainPlayer.get()?.lookTo({
        hlookat: view.hlookat,
        vlookat: view.vlookat,
        fov: view.fov,
      });
    }
  }, []);

  useEffect(() => {
    return onHotspotClick.watch(({ id }) => {
      handleItemToggle(id);
    });
  }, [hotspots.type, hotspots.list]);

  const statusAll: SelectStatus = (() => {
    if (hotspots.type === 1) return SelectStatus.all;
    if (!hotspots.list.length) return SelectStatus.none;
    return SelectStatus.part;
  })();

  const nameHandler = useInputHandlers((val) => {
    setState((draft) => {
      draft.name = val;
    });
  });

  const handleConfirm = () => {
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        draft.groups ??= {};
        draft.groups.list ??= [];
        draft.groups.list[index] = state;
      },
    });
    onRequestClose();
  };

  const handleDelete = () => {
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        draft.groups ??= {};
        draft.groups.list ??= [];
        draft.groups.list.splice(index, 1);
      },
    });
  };

  const handleSelectAll = (status: SelectStatus) => {
    setState((draft) => {
      draft.hotspots.type = status === SelectStatus.all ? 0 : 1;
      draft.hotspots.list = [];
    });
  };

  const handleItemToggle = (id: string) => {
    if (hotspots.type === 1) {
      setState((draft) => {
        draft.hotspots.type = 0;
        draft.hotspots.list = hotspotList
          .map((x) => x.id)
          .filter((_id) => _id !== id);
      });
    } else {
      setState((draft) => {
        draft.hotspots.type = 0;
        if (hotspots.list.includes(id)) {
          draft.hotspots.list = draft.hotspots.list.filter((_id) => _id !== id);
        } else {
          draft.hotspots.list.push(id);
        }
      });
    }
  };

  const renderItem = (item: Hotspot) => {
    const isOn = hotspots.type === 1 || hotspots.list.includes(item.id);
    return (
      <div
        key={item.id}
        className="mb-10 flex h-35 w-full cursor-pointer items-center rounded bg-black-#34 px-10"
        onClick={() => handleItemToggle(item.id)}
      >
        <CheckboxNoFunc className="mr-5 shrink-0" checked={isOn} />
        <ItemIcon icon={item.icon} />
        <div className="mx-10 truncate text-base text-white">{item.title}</div>
        <div className="ml-auto flex-shrink-0 text-sm text-black-gray">
          {getFeatName(item.data.code)}
        </div>
      </div>
    );
  };

  return (
    <PanelBase
      isChanged={isChanged}
      title={<Title>分组设置</Title>}
      onRequestClose={onRequestClose}
      onDeleteClick={handleDelete}
      onConfirm={handleConfirm}
    >
      <Collapse title="分组名称">
        <Validator
          value={name}
          validFn={(val) => !!val.trim()}
          warningMessage="请输入分组名称"
        >
          {({ value, warning }) => (
            <Input
              scheme="dark"
              className="h-40 w-full"
              placeholder="请输入分组名称"
              maxLength={50}
              value={value}
              warning={warning}
              {...nameHandler}
            />
          )}
        </Validator>
      </Collapse>
      <Collapse title="分组视角">
        <FeatureToggle
          className="mb-10"
          title="自定义分组视角"
          isOn={view.type === 1}
          onToggle={(bool: boolean) => {
            setState((draft) => {
              draft.view = { type: toFlag(bool) };
            });
          }}
        />
        {!!view.type && (
          <ViewSetting
            scene={scene}
            view={view}
            onChange={(newView) => {
              setState((draft) => {
                draft.view.fov = newView.fov;
                draft.view.hlookat = newView.hlookat;
                draft.view.vlookat = newView.vlookat;
              });
            }}
          />
        )}
        {!!view.type && (
          <FeatureToggle
            title="分组视角缩放"
            isOn={view.changeFov === 1}
            onToggle={(bool: boolean) => {
              setState((draft) => {
                draft.view.changeFov = toFlag(bool);
              });
            }}
          />
        )}
      </Collapse>
      <Collapse title="分组热点">
        <div className="mb-10 flex h-35 w-full cursor-pointer items-center rounded bg-black-#34 px-10">
          <Checkbox label="全选" status={statusAll} onClick={handleSelectAll} />
        </div>
        {hotspotList.map(renderItem)}
      </Collapse>
    </PanelBase>
  );
}

type GroupView = HotspotGroupItem['view'];

const hasView = (
  view: GroupView
): view is SetRequired<GroupView, 'hlookat' | 'vlookat' | 'fov'> => {
  return ![view.hlookat, view.vlookat, view.fov].includes(undefined);
};

function ViewSetting({
  scene,
  view,
  onChange,
}: {
  scene: SceneData;
  view: GroupView;
  onChange: (val: LookToView) => void;
}) {
  const previewRef = useRef<KrpPlayer | null>(null);
  useEffect(() => {
    const unwatch = groupViewCatpure.watch((krpView) => {
      onChange(krpView);
    });
    return () => {
      unwatch();
    };
  }, []);
  const hasGroupView = hasView(view);

  useGate($GateGroupViewCapture, true);

  useEffect(() => {
    if (!hasGroupView) return;
    const data = produce(scene, (draft) => {
      draft.view.hLookAt = view.hlookat;
      draft.view.vLookAt = view.vlookat;
      draft.view.fov = view.fov;
      draft.view.flatFovType = MatrixFovType.custom;
    });
    if (!previewRef.current) {
      const player = new KrpanoPlayer() as KrpPlayer;
      player.createPlayer('group-view', {}, () => {
        previewRef.current = player;
        player.enableControl(false);
        loadScene({ player, data });
      });
    } else {
      loadScene({ player: previewRef.current, data });
    }
  }, [scene.materialId, view]);

  const onTurnViewClick = () => {
    MainPlayer.get()?.lookTo({
      hlookat: view.hlookat,
      vlookat: view.vlookat,
      fov: view.fov,
    });
  };

  const [container] = useHover((isHovered) => (
    <div className="relative flex h-full w-full items-center justify-center">
      <div id="group-view" className="h-full w-full" />
      {isHovered && hasGroupView && (
        <Button
          className="absolute"
          size="sm"
          scheme="gray"
          onClick={onTurnViewClick}
        >
          定位到分组视角
        </Button>
      )}
    </div>
  ));

  return (
    <Validator
      value={view}
      validFn={(value) => {
        if (value.type === 0) return true;
        return hasView(value);
      }}
      warningMessage="请设置分组视角"
    >
      {({ value, warning }) => (
        <ImageContainer
          className="mb-10 h-115"
          placeholder="还未设置分组初始视角"
          warning={warning}
        >
          {hasView(value) && container}
        </ImageContainer>
      )}
    </Validator>
  );
}
